import {
  ArrowRightIcon,
  BodyText,
  Button,
  Caption,
  Card,
  ChevronRightIcon,
  Flex_unstable as Flex,
  Heading,
  RichIcon,
} from '@farol-ds/react'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { useDispatcher } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useRef, useState } from 'react'

import { buildWhatsappRedirect } from '../../utils/whatsapp-redirect'
import styles from './sd-quiz.module.scss'

export interface SDQuizOption {
  label: string
  onSelect?: () => void
}

export interface SDQuizQuestion {
  question: string
  description?: string
  options: SDQuizOption[]
}

export interface SDQuizFinalStep {
  title: string
  description: string
  ctaLabel: string
}

export interface SDQuizProps {
  questions: SDQuizQuestion[]
  finalStepText: SDQuizFinalStep
  whatsappMessage: string
  docId?: number | string
}

const PAGE = 'docview-artigo'
const APP_ID = 'web-docview'
const ACTION = 'sd_quiz'
const EXPERIMENT = 'sd-docview-chats'
const VARIANT = 'variantB'

export function SDQuiz({ questions, finalStepText, whatsappMessage, docId }: SDQuizProps) {
  const [stepIndex, setStepIndex] = useState(0)
  const session = useUserSession()
  const { ref: dispatcherRef, sendMetrics } = useDispatcher<HTMLDivElement>()
  const isOnScreen = useOnScreen(dispatcherRef, { threshold: 0.5 }, false)

  const viewedQuestionSteps = useRef<Set<number>>(new Set())
  const finalViewSent = useRef(false)
  const finalSubmitted = useRef(false)

  const isFinal = stepIndex >= questions.length
  const userId = session?.pid != null ? String(session.pid) : null

  const baseMetadata = useMemo(
    () => ({
      page: PAGE,
      action: ACTION,
      doc_id: docId ?? null,
      experiment: EXPERIMENT,
      variant: VARIANT,
      total_steps: questions.length,
    }),
    [docId, questions.length]
  )

  const sendMetricsRef = useRef(sendMetrics)
  sendMetricsRef.current = sendMetrics

  useEffect(() => {
    if (isFinal || !isOnScreen) return
    if (viewedQuestionSteps.current.has(stepIndex)) return

    sendMetricsRef.current('SeusDireitos.CtaView', userId, {
      app_id: APP_ID,
      page: PAGE,
      metadata: { ...baseMetadata, type: 'question', current_step: stepIndex + 1 },
    })
    viewedQuestionSteps.current.add(stepIndex)
  }, [isOnScreen, isFinal, stepIndex, baseMetadata, userId])

  useEffect(() => {
    if (isFinal && !finalViewSent.current) {
      sendMetricsRef.current('SeusDireitos.CtaView', userId, {
        app_id: APP_ID,
        page: PAGE,
        metadata: { ...baseMetadata, type: 'redirect' },
      })
      finalViewSent.current = true
    }
  }, [isFinal, baseMetadata, userId])

  const handleSelect = (option: SDQuizOption) => {
    option.onSelect?.()
    sendMetrics('SeusDireitos.CtaClick', userId, {
      app_id: APP_ID,
      page: PAGE,
      metadata: { ...baseMetadata, type: 'question', current_step: stepIndex + 1 },
    })
    setStepIndex((i) => i + 1)
  }

  const handleContinue = () => {
    if (!finalSubmitted.current) {
      finalSubmitted.current = true

      sendMetrics('SeusDireitos.CtaClick', userId, {
        app_id: APP_ID,
        page: PAGE,
        metadata: { ...baseMetadata, type: 'redirect' },
      })
      sendMetrics('SeusDireitos.QuestionSent', userId, {
        app_id: APP_ID,
        page: PAGE,
        metadata: baseMetadata,
      })
    }

    const href = buildWhatsappRedirect({
      message: whatsappMessage,
      deviceId: session?.jdid,
      userId: session?.pid != null ? String(session.pid) : undefined,
    })
    if (typeof window !== 'undefined') {
      window.open(href, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Card ref={dispatcherRef} className={styles.root}>
      <Flex direction="column" gap="2xs">
        {isFinal ? (
          <>
            <Flex direction="column" gap="6xs">
              <Heading size="md">{finalStepText.title}</Heading>
              <BodyText size="md">{finalStepText.description}</BodyText>
            </Flex>
            <Button
              kind="primary"
              size="md"
              rightIcon={<ArrowRightIcon />}
              block
              onClick={handleContinue}
            >
              {finalStepText.ctaLabel}
            </Button>
          </>
        ) : (
          <>
            <Flex direction="column" gap="6xs">
              <Caption>
                Pergunta {stepIndex + 1} de {questions.length}
              </Caption>
              <Heading size="md">{questions[stepIndex].question}</Heading>
              {questions[stepIndex].description && (
                <BodyText size="md">{questions[stepIndex].description}</BodyText>
              )}
            </Flex>
            <Flex direction="column" gap="5xs">
              {questions[stepIndex].options.map((option, index) => (
                <Card key={index} className={styles.optionCard} disablePadding asChild>
                  <button
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={styles.option}
                  >
                    <BodyText size="md">{option.label}</BodyText>
                    <RichIcon size="sm" type="primary" icon={ChevronRightIcon} aria-hidden="true" />
                  </button>
                </Card>
              ))}
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  )
}
